/**
 * Fase3Test Component
 * 
 * Test and demonstration component for Fase 3 functionality:
 * - ActivityQueueManager with merge logic
 * - RackCarouselController with LRU cache
 * - QueueIndicator visual component
 * 
 * Simulates incoming activity events and demonstrates automatic rack switching.
 * 
 * @author BizClick WMS
 * @version 1.25 - Fase 3 Implementation
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Square, Plus, Trash2, BarChart3 } from 'lucide-react';
import { useLocationActivity } from '../../../../hooks/useLocationActivity';
import { LocationActivityEvent, ActivityType } from '../../../../types/locationActivity';
import { ActivityQueueManager } from './ActivityQueueManager';
import { RackCarouselController, RackLoader } from './RackCarouselController';
import { QueueIndicator } from './QueueIndicator';
import { RackStructure } from '../rack-builder/types';
import { QueueStats, CarouselState, CacheStats } from './types';

/**
 * Mock rack loader for testing
 */
const mockRackLoader: RackLoader = async (warehouseId: string, rackNumber: string) => {
  console.log(`[MockRackLoader] Loading rack ${rackNumber} from ${warehouseId}...`);
  
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Create mock rack structure
  const structure: RackStructure = {
    rackNumber: rackNumber,
    locations: [],
    verticals: [1, 2, 3, 4, 5],
    shelves: [1, 2, 3, 4],
    gridWidth: 5,
    gridHeight: 4
  };
  
  console.log(`[MockRackLoader] Rack ${rackNumber} loaded`);
  return structure;
};

export const Fase3Test: React.FC = () => {
  // Queue Manager
  const queueManagerRef = useRef<ActivityQueueManager>(
    new ActivityQueueManager({ dwellTime: 5000, maxQueueSize: 20 })
  );
  
  // Carousel Controller
  const carouselControllerRef = useRef<RackCarouselController>(
    new RackCarouselController(mockRackLoader, { dwellTime: 5000, maxCacheSize: 10 })
  );
  
  // State
  const [queueStats, setQueueStats] = useState<QueueStats>({
    queueLength: 0,
    uniqueRacks: 0,
    totalEvents: 0,
    avgEventsPerRack: 0
  });
  
  const [carouselState, setCarouselState] = useState<CarouselState>(
    carouselControllerRef.current.getState()
  );
  
  const [cacheStats, setCacheStats] = useState<CacheStats>(
    carouselControllerRef.current.getCacheStats()
  );
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingTimer, setProcessingTimer] = useState<NodeJS.Timeout | null>(null);
  const [eventLog, setEventLog] = useState<string[]>([]);
  
  // Connection to SignalR
  const handleActivityReceived = useCallback((event: LocationActivityEvent) => {
    console.log('[Fase3Test] Activity received:', event);
    addLogEntry(`📥 Event mottatt: Reol ${event.rackNumber}, ${event.binCode}`);
    
    // Enqueue event
    const result = queueManagerRef.current.enqueue(event);
    if (result.success) {
      setQueueStats(queueManagerRef.current.getStats());
      addLogEntry(`✅ ${result.message}`);
    } else {
      addLogEntry(`❌ ${result.message}`);
    }
  }, []);
  
  useLocationActivity(handleActivityReceived);
  
  /**
   * Setup carousel state change listener
   */
  useEffect(() => {
    carouselControllerRef.current.setStateChangeCallback((state) => {
      setCarouselState(state);
      setCacheStats(carouselControllerRef.current.getCacheStats());
    });
  }, []);
  
  /**
   * Add log entry
   */
  const addLogEntry = (message: string) => {
    const timestamp = new Date().toLocaleTimeString('nb-NO');
    setEventLog(prev => [`[${timestamp}] ${message}`, ...prev].slice(0, 30));
  };
  
  /**
   * Process queue - dequeue and switch to next rack
   */
  const processQueue = useCallback(async () => {
    if (queueManagerRef.current.isEmpty()) {
      addLogEntry('⚠️ Ingen reoler i køen');
      return;
    }
    
    const nextItem = queueManagerRef.current.dequeue();
    if (!nextItem) return;
    
    addLogEntry(`🔄 Bytter til reol ${nextItem.rackNumber}...`);
    setQueueStats(queueManagerRef.current.getStats());
    
    const result = await carouselControllerRef.current.switchToRack(nextItem);
    if (result.success) {
      addLogEntry(`✅ ${result.message}`);
      setCarouselState(carouselControllerRef.current.getState());
      setCacheStats(carouselControllerRef.current.getCacheStats());
    } else {
      addLogEntry(`❌ ${result.message}`);
    }
  }, []);
  
  /**
   * Start automatic processing
   */
  const startProcessing = () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    addLogEntry('▶️ Startet automatisk prosessering');
    
    const timer = setInterval(async () => {
      if (!queueManagerRef.current.isEmpty()) {
        await processQueue();
      }
    }, 6000); // Process every 6 seconds (dwell time + margin)
    
    setProcessingTimer(timer);
  };
  
  /**
   * Stop automatic processing
   */
  const stopProcessing = () => {
    if (processingTimer) {
      clearInterval(processingTimer);
      setProcessingTimer(null);
    }
    setIsProcessing(false);
    addLogEntry('⏹️ Stoppet automatisk prosessering');
  };
  
  /**
   * Toggle pause
   */
  const handleTogglePause = () => {
    if (carouselState.isPaused) {
      carouselControllerRef.current.resume();
      addLogEntry('▶️ Fortsetter automatisk bytte');
    } else {
      carouselControllerRef.current.pause();
      addLogEntry('⏸️ Pause automatisk bytte');
    }
  };
  
  /**
   * Simulate event
   */
  const simulateEvent = () => {
    const rackNumbers = [5, 7, 12, 15, 20];
    const activityTypes = [
      ActivityType.Pick,
      ActivityType.PutAway,
      ActivityType.Transfer,
      ActivityType.CorrectionIn
    ];
    
    const randomRack = rackNumbers[Math.floor(Math.random() * rackNumbers.length)];
    const randomType = activityTypes[Math.floor(Math.random() * activityTypes.length)];
    
    const mockEvent: LocationActivityEvent = {
      binCode: `WH01-R${randomRack.toString().padStart(2, '0')}-V02-S03`,
      rackNumber: randomRack,
      activityType: randomType,
      timestamp: new Date().toISOString(),
      operatorId: 'TEST_USER',
      itemNo: `ITEM${Math.floor(Math.random() * 1000)}`,
      quantity: Math.floor(Math.random() * 10) + 1,
      warehouseId: 'WH01',
      companyNo: 'TEST',
      customerNo: 'TEST'
    };
    
    handleActivityReceived(mockEvent);
  };
  
  /**
   * Clear queue
   */
  const clearQueue = () => {
    queueManagerRef.current.clear();
    setQueueStats(queueManagerRef.current.getStats());
    addLogEntry('🗑️ Køen er tømt');
  };
  
  /**
   * Clear cache
   */
  const clearCache = () => {
    carouselControllerRef.current.clearCache();
    setCacheStats(carouselControllerRef.current.getCacheStats());
    addLogEntry('🗑️ Cache er tømt');
  };
  
  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (processingTimer) {
        clearInterval(processingTimer);
      }
      carouselControllerRef.current.destroy();
    };
  }, [processingTimer]);
  
  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Fase 3 Test</h1>
          <p className="text-sm text-gray-600 mt-1">
            Queue Management & Carousel Controller
          </p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={simulateEvent}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Simuler Event
          </button>
          
          {!isProcessing ? (
            <button
              onClick={startProcessing}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              Start Auto
            </button>
          ) : (
            <button
              onClick={stopProcessing}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors flex items-center gap-2"
            >
              <Square className="w-4 h-4" />
              Stopp Auto
            </button>
          )}
        </div>
      </div>
      
      {/* Queue Indicator */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Queue Indicator</h2>
        <QueueIndicator
          queueStats={queueStats}
          isAutoSwitching={carouselState.isAutoSwitching}
          isPaused={carouselState.isPaused}
          onTogglePause={handleTogglePause}
          showTooltip={true}
        />
      </div>
      
      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Queue Stats */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-800">Køstatistikk</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Reoler i kø:</span>
              <span className="font-medium text-gray-800">{queueStats.queueLength}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Totale events:</span>
              <span className="font-medium text-gray-800">{queueStats.totalEvents}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Snitt per reol:</span>
              <span className="font-medium text-gray-800">
                {queueStats.avgEventsPerRack.toFixed(1)}
              </span>
            </div>
            <button
              onClick={clearQueue}
              className="w-full mt-3 px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Tøm kø
            </button>
          </div>
        </div>
        
        {/* Carousel State */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Carousel Status</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Aktiv reol:</span>
              <span className="font-medium text-gray-800">
                {carouselState.currentRack ?? 'Ingen'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Auto-bytte:</span>
              <span className={`font-medium ${
                carouselState.isAutoSwitching ? 'text-green-600' : 'text-gray-400'
              }`}>
                {carouselState.isAutoSwitching ? 'Aktivert' : 'Deaktivert'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Status:</span>
              <span className={`font-medium ${
                carouselState.isPaused ? 'text-amber-600' : 'text-green-600'
              }`}>
                {carouselState.isPaused ? 'Pause' : 'Kjører'}
              </span>
            </div>
            <button
              onClick={processQueue}
              disabled={queueStats.queueLength === 0}
              className="w-full mt-3 px-3 py-1.5 bg-blue-100 text-blue-700 text-sm rounded hover:bg-blue-200 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Bytt til neste reol
            </button>
          </div>
        </div>
        
        {/* Cache Stats */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Cache Statistikk</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Cache størrelse:</span>
              <span className="font-medium text-gray-800">
                {cacheStats.size}/{cacheStats.maxSize}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Hit rate:</span>
              <span className="font-medium text-gray-800">
                {cacheStats.hitRate.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Evictions:</span>
              <span className="font-medium text-gray-800">{cacheStats.evictions}</span>
            </div>
            <button
              onClick={clearCache}
              className="w-full mt-3 px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Tøm cache
            </button>
          </div>
        </div>
      </div>
      
      {/* Event Log */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Event Logg</h3>
          <button
            onClick={() => setEventLog([])}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Tøm logg
          </button>
        </div>
        <div className="bg-gray-50 rounded p-4 h-64 overflow-y-auto font-mono text-xs">
          {eventLog.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              Ingen events ennå. Klikk "Simuler Event" for å starte.
            </div>
          ) : (
            <div className="space-y-1">
              {eventLog.map((entry, index) => (
                <div key={index} className="text-gray-700">
                  {entry}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Fase3Test;
